import {useState, type ReactNode} from 'react';
import styled, {keyframes} from 'styled-components';
import {MaplePlush} from '../mascot/MaplePlush';
import {MarblesPlush} from '../mascot/MarblesPlush';
import {OlliePlush} from '../mascot/OlliePlush';
import {PaiPlush} from '../mascot/PaiPlush';
import {PennyPlush} from '../mascot/PennyPlush';
import {PlushPi} from '../mascot/PlushPi';
import {PuddlePlush} from '../mascot/PuddlePlush';
import {CANDY, FELT, FeltDefs, useFeltIds} from './felt';
import {Bounce, ToyShell, type MapToyProps} from './mapToys';

/**
 * The mystery egg: a felt Easter egg that wobbles and cracks as you click
 * it, then hatches a random plush friend in a burst of confetti, drawn with
 * gacha-style drop rates (one of them a legendary golden Ollie). It keeps a
 * collection as a row of dots that fill in as friends are found (all gold
 * once complete). Nothing is written on the toy; its accessible name carries
 * the details, including, on completion, the eggs needed against the coupon
 * collector's expectation for these exact drop rates.
 */

/** Clicks it takes to hatch an egg. */
const CRACKS_TO_HATCH = 3;

/** Someone who can hatch, with a drop weight in percent. */
interface Hatchling {
  id: string;
  name: string;
  percent: number;
  /** Color of this hatchling's dot in the collection row. */
  dot: string;
  isLegendary?: boolean;
  render: (size: number) => ReactNode;
}

/** The most common hatchling, also the fallback for the weighted draw. */
const BEANIE: Hatchling = {
  id: 'beanie',
  dot: FELT.cardinal,
  name: 'Beanie',
  percent: 20,
  render: size => <PaiPlush size={size} state="happy" />,
};

const HATCHLINGS: readonly Hatchling[] = [
  BEANIE,
  {
    id: 'ollie',
    dot: '#A87A52',
    name: 'Ollie',
    percent: 18,
    render: size => <OlliePlush size={size} state="happy" />,
  },
  {
    id: 'plush-pi',
    dot: FELT.creamDark,
    name: 'Plush π',
    percent: 15,
    render: size => <PlushPi size={size} state="happy" />,
  },
  {
    id: 'penny',
    dot: '#3F5170',
    name: 'Penny',
    percent: 13,
    render: size => <PennyPlush size={size} state="happy" />,
  },
  {
    id: 'maple',
    dot: '#C9652F',
    name: 'Maple',
    percent: 12,
    render: size => <MaplePlush size={size} state="happy" />,
  },
  {
    id: 'puddle',
    dot: '#6DB257',
    name: 'Puddle',
    percent: 11,
    render: size => <PuddlePlush size={size} state="happy" />,
  },
  {
    id: 'marbles',
    dot: '#F29BB3',
    name: 'Marbles',
    percent: 8,
    render: size => <MarblesPlush size={size} state="happy" />,
  },
  {
    id: 'golden-ollie',
    dot: '#E8B53A',
    name: 'golden Ollie',
    percent: 3,
    isLegendary: true,
    render: size => (
      <div
        style={{
          filter:
            'sepia(0.85) saturate(2.2) hue-rotate(8deg) brightness(1.18) drop-shadow(0 0 6px #FFD35C)',
        }}
      >
        <OlliePlush size={size} state="happy" hasBeanie />
      </div>
    ),
  },
];

/**
 * Expected eggs to collect every hatchling, by inclusion–exclusion over the
 * drop probabilities: E = Σ over nonempty subsets S of (−1)^(|S|+1) / P(S).
 */
const EXPECTED_EGGS_TO_COLLECT_ALL = (() => {
  const probabilities = HATCHLINGS.map(hatchling => hatchling.percent / 100);
  let expected = 0;
  for (let mask = 1; mask < 1 << probabilities.length; mask++) {
    let subsetProbability = 0;
    let size = 0;
    probabilities.forEach((probability, index) => {
      if ((mask & (1 << index)) !== 0) {
        subsetProbability += probability;
        size++;
      }
    });
    expected += (size % 2 === 1 ? 1 : -1) / subsetProbability;
  }
  return expected;
})();

function rarity(percent: number): string {
  if (percent >= 18) {
    return 'common';
  }
  if (percent >= 12) {
    return 'uncommon';
  }
  if (percent >= 8) {
    return 'rare';
  }
  return 'LEGENDARY ✨';
}

/** Picks a hatchling by drop weight. */
function drawHatchling(): Hatchling {
  let draw = Math.random() * 100;
  const picked = HATCHLINGS.find(hatchling => {
    draw -= hatchling.percent;
    return draw < 0;
  });
  return picked ?? BEANIE;
}

const wobble = keyframes`
  0%, 100% { transform: rotate(0); }
  20% { transform: rotate(-14deg); }
  45% { transform: rotate(11deg); }
  70% { transform: rotate(-6deg); }
`;

const flyOff = keyframes`
  0% { transform: translate(0, 0) rotate(0); opacity: 1; }
  100% { transform: translate(-45%, -90%) rotate(-70deg); opacity: 0; }
`;

const burst = keyframes`
  0% { transform: translate(-50%, -50%) scale(1); opacity: 1; }
  100% {
    transform: translate(calc(-50% + var(--dx)), calc(-50% + var(--dy)))
      scale(0.35) rotate(var(--spin));
    opacity: 0;
  }
`;

const Stage = styled.div<{$size: number}>`
  position: relative;
  width: ${({$size}) => $size}px;
  height: ${({$size}) => $size * 1.2}px;
  display: flex;
  align-items: flex-end;
  justify-content: center;
`;

const twinkle = keyframes`
  0%, 100% { transform: scale(0.4) rotate(0); opacity: 0; }
  50% { transform: scale(1.1) rotate(90deg); opacity: 1; }
`;

/** A twinkling star around a legendary hatchling. */
const Sparkle = styled.svg<{$left: number; $top: number; $delaySec: number}>`
  position: absolute;
  left: ${({$left}) => $left}%;
  top: ${({$top}) => $top}%;
  pointer-events: none;
  filter: drop-shadow(0 0 3px #fff3c4);
  animation: ${twinkle} 1.4s ease-in-out ${({$delaySec}) => $delaySec}s infinite;

  @media (prefers-reduced-motion: reduce) {
    animation: none;
  }
`;

/** Lifts the hatchling so only its feet sit in the shell cup. */
const Seat = styled.div<{$lift: number}>`
  position: relative;
  z-index: 0;
  margin-bottom: ${({$lift}) => $lift}px;
`;

const Wobble = styled.div`
  transform-origin: center bottom;
  animation: ${wobble} 0.5s ease-in-out;

  @media (prefers-reduced-motion: reduce) {
    animation: none;
  }
`;

const ShellTop = styled.svg`
  position: absolute;
  left: 0;
  bottom: 0;
  pointer-events: none;
  animation: ${flyOff} 0.8s ease-out forwards;

  @media (prefers-reduced-motion: reduce) {
    display: none;
  }
`;

/** The collection: one dot per hatchling, filled once found. */
const DotRow = styled.div`
  display: flex;
  justify-content: center;
  gap: 3px;
  margin-top: 3px;
`;

const Dot = styled.span<{$color: string; $isFound: boolean; $size: number}>`
  width: ${({$size}) => $size}px;
  height: ${({$size}) => $size}px;
  border-radius: 50%;
  background: ${({$color, $isFound}) =>
    $isFound ? $color : 'rgba(255,255,255,0.7)'};
  border: 1.5px solid ${({$color}) => $color};
  transition: background 0.3s;
`;

const ShellCup = styled.svg`
  position: absolute;
  left: 0;
  bottom: 0;
  pointer-events: none;
`;

const Confetti = styled.span<{
  $dx: number;
  $dy: number;
  $spin: number;
  $color: string;
  $isRound: boolean;
}>`
  --dx: ${({$dx}) => $dx}px;
  --dy: ${({$dy}) => $dy}px;
  --spin: ${({$spin}) => $spin}deg;
  position: absolute;
  left: 50%;
  top: 55%;
  width: 11px;
  height: 11px;
  background: ${({$color}) => $color};
  border-radius: ${({$isRound}) => ($isRound ? '50%' : '3px')};
  pointer-events: none;
  animation: ${burst} 0.9s cubic-bezier(0.2, 0.7, 0.3, 1) forwards;

  @media (prefers-reduced-motion: reduce) {
    display: none;
  }
`;

/** Egg outline in the egg's 100×120 viewBox. */
const EGG =
  'M 50 6 C 76 6 92 48 92 76 C 92 102 74 116 50 116 ' +
  'C 26 116 8 102 8 76 C 8 48 24 6 50 6 Z';

/** The zigzag the shell splits along. */
const SPLIT = 'M 8 70 L 20 60 L 32 72 L 44 60 L 56 72 L 68 60 L 80 72 L 92 62';

/** Crack lines, revealed one per click. */
const CRACKS = [
  'M 50 22 L 44 34 L 52 42 L 46 52',
  'M 70 40 L 62 50 L 70 58 L 62 66',
  'M 26 48 L 34 58 L 26 64 L 34 72',
] as const;

/** The egg's painted decoration: stripes and polka dots. */
function EggPaint() {
  return (
    <g>
      <path
        d="M 14 52 Q 50 40 86 52"
        stroke={FELT.pink}
        strokeWidth={7}
        fill="none"
        strokeLinecap="round"
      />
      <path
        d="M 10 90 Q 50 102 90 90"
        stroke={FELT.mint}
        strokeWidth={7}
        fill="none"
        strokeLinecap="round"
      />
      {[
        {cx: 38, cy: 26, color: FELT.gold},
        {cx: 62, cy: 30, color: FELT.blue},
        {cx: 30, cy: 74, color: FELT.cardinal},
        {cx: 52, cy: 70, color: FELT.gold},
        {cx: 72, cy: 76, color: FELT.blue},
        {cx: 44, cy: 104, color: FELT.pink},
        {cx: 64, cy: 102, color: FELT.cardinal},
      ].map(({cx, cy, color}) => (
        <circle
          key={`${cx}-${cy}`}
          cx={cx}
          cy={cy}
          r={4.5}
          fill={color}
          opacity={0.9}
        />
      ))}
    </g>
  );
}

/**
 * Renders the mystery egg toy (see module comment).
 *
 * @param props - See {@link MapToyProps}.
 * @return The toy.
 * @throws Never.
 */
export function MysteryEgg({size, isPreview = false}: MapToyProps) {
  const ids = useFeltIds();
  const [cracks, setCracks] = useState(0);
  const [hatched, setHatched] = useState<Hatchling>();
  const [eggsOpened, setEggsOpened] = useState(0);
  const [found, setFound] = useState<ReadonlySet<string>>(() => new Set());
  const [completedAt, setCompletedAt] = useState<number>();
  const [pokeCount, setPokeCount] = useState(0);

  const poke = () => {
    setPokeCount(pokeCount + 1);
    if (hatched !== undefined) {
      // A fresh egg rolls in.
      setHatched(undefined);
      setCracks(0);
      return;
    }
    if (cracks + 1 < CRACKS_TO_HATCH) {
      setCracks(cracks + 1);
      return;
    }
    const hatchling = drawHatchling();
    const nextFound = new Set(found).add(hatchling.id);
    const opened = eggsOpened + 1;
    setHatched(hatchling);
    setEggsOpened(opened);
    setFound(nextFound);
    if (completedAt === undefined && nextFound.size === HATCHLINGS.length) {
      setCompletedAt(opened);
    }
  };

  let caption: string;
  if (hatched === undefined) {
    caption = ['a mystery egg…', 'it wiggled!', 'almost…'][cracks] ?? '…';
  } else if (completedAt !== undefined && completedAt === eggsOpened) {
    caption = `all ${HATCHLINGS.length} in ${completedAt} eggs! avg ≈ ${Math.round(EXPECTED_EGGS_TO_COLLECT_ALL)}`;
  } else {
    caption = `${hatched.name} · ${rarity(hatched.percent)} ${hatched.percent}% · ${found.size}/${HATCHLINGS.length}`;
  }

  const confettiColors = hatched?.isLegendary
    ? ['#E8B53A', '#FFE08A', '#F4C542', '#FFF6D0']
    : CANDY;
  const confettiCount = hatched?.isLegendary ? 36 : 22;
  const eggWidth = size;
  const eggHeight = size * 1.2;

  return (
    <ToyShell
      isPreview={isPreview}
      label="Mystery egg"
      caption={caption}
      onPoke={poke}
    >
      <Stage $size={size}>
        {hatched === undefined ? (
          <Wobble key={pokeCount}>
            <svg
              viewBox="0 0 100 120"
              width={eggWidth}
              height={eggHeight}
              aria-hidden
            >
              <defs>
                <FeltDefs ids={ids} />
                <clipPath id={`${ids.fuzz}-egg`}>
                  <path d={EGG} />
                </clipPath>
              </defs>
              <ellipse cx={50} cy={116} rx={30} ry={4} fill="rgba(0,0,0,0.1)" />
              <path
                d={EGG}
                fill={FELT.creamLight}
                filter={`url(#${ids.fuzz})`}
              />
              <g clipPath={`url(#${ids.fuzz}-egg)`}>
                <EggPaint />
              </g>
              <path
                d="M 34 20 Q 26 34 24 50"
                stroke="#fff"
                strokeWidth={4}
                strokeLinecap="round"
                fill="none"
                opacity={0.7}
              />
              {CRACKS.slice(0, cracks).map(d => (
                <path
                  key={d}
                  d={d}
                  stroke={FELT.ink}
                  strokeWidth={2.2}
                  strokeLinecap="round"
                  strokeLinejoin="round"
                  fill="none"
                />
              ))}
            </svg>
          </Wobble>
        ) : (
          <>
            <Seat $lift={size * 0.3}>
              <Bounce key={eggsOpened} $animation="pop">
                {hatched.render(size * 0.88)}
              </Bounce>
            </Seat>
            {hatched.isLegendary &&
              [
                {left: 8, top: 10, delaySec: 0},
                {left: 82, top: 4, delaySec: 0.35},
                {left: 90, top: 46, delaySec: 0.7},
                {left: 2, top: 52, delaySec: 1.05},
              ].map(({left, top, delaySec}) => (
                <Sparkle
                  key={`${left}-${top}`}
                  $left={left}
                  $top={top}
                  $delaySec={delaySec}
                  viewBox="0 0 20 20"
                  width={16}
                  height={16}
                  aria-hidden
                >
                  <path
                    d="M 10 0 Q 11.5 8.5 20 10 Q 11.5 11.5 10 20 Q 8.5 11.5 0 10 Q 8.5 8.5 10 0 Z"
                    fill="#FFD35C"
                  />
                </Sparkle>
              ))}
            <ShellCup
              key={`cup-${eggsOpened}`}
              viewBox="0 0 100 120"
              width={eggWidth}
              height={eggHeight}
              aria-hidden
            >
              <defs>
                <clipPath id={`${ids.fuzz}-cup`}>
                  <path d={`${SPLIT} L 100 120 L 0 120 Z`} />
                </clipPath>
              </defs>
              <g clipPath={`url(#${ids.fuzz}-cup)`} transform="translate(0 8)">
                <path d={EGG} fill={FELT.creamLight} />
                <EggPaint />
                <path
                  d={SPLIT}
                  stroke={FELT.creamDark}
                  strokeWidth={2}
                  fill="none"
                />
              </g>
            </ShellCup>
            <ShellTop
              key={`top-${eggsOpened}`}
              viewBox="0 0 100 120"
              width={eggWidth}
              height={eggHeight}
              aria-hidden
            >
              <defs>
                <clipPath id={`${ids.fuzz}-top`}>
                  <path d={`${SPLIT} L 100 0 L 0 0 Z`} />
                </clipPath>
              </defs>
              <g clipPath={`url(#${ids.fuzz}-top)`}>
                <path d={EGG} fill={FELT.creamLight} />
                <EggPaint />
              </g>
            </ShellTop>
            {Array.from({length: confettiCount}, (_, index) => {
              const angle = (index / confettiCount) * Math.PI * 2;
              const distance = size * (0.55 + (index % 3) * 0.18);
              return (
                <Confetti
                  // Confetti pieces are fixed slots in the burst.
                  // eslint-disable-next-line @eslint-react/no-array-index-key
                  key={`${eggsOpened}-${index}`}
                  $dx={Math.cos(angle) * distance}
                  $dy={Math.sin(angle) * distance - size * 0.2}
                  $spin={(index % 2 === 0 ? 1 : -1) * 220}
                  $color={
                    confettiColors[index % confettiColors.length] ?? FELT.gold
                  }
                  $isRound={index % 3 === 0}
                />
              );
            })}
          </>
        )}
      </Stage>
      <DotRow aria-hidden>
        {HATCHLINGS.map(hatchling => (
          <Dot
            key={hatchling.id}
            $color={
              completedAt !== undefined && completedAt === eggsOpened
                ? '#E8B53A'
                : hatchling.dot
            }
            $isFound={found.has(hatchling.id)}
            $size={Math.max(5, size * 0.075)}
          />
        ))}
      </DotRow>
    </ToyShell>
  );
}
